import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Save, Sparkles, Send } from "lucide-react";
import { Link } from "react-router-dom";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";
const INPUT = "w-full bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl focus:ring-2 focus:ring-[#0B5E4E]/20 outline-none px-4 py-2.5 text-sm";
const LABEL = "text-sm font-medium";

const render = (tpl, cfg) => (tpl || "")
  .replaceAll("{first_name}", "Ada").replaceAll("{church}", cfg.church || "")
  .replaceAll("{service_times}", cfg.service_times || "").replaceAll("{address}", cfg.address || "")
  .replaceAll("{portal_link}", cfg.portal_link || "");

const Preview = ({ text, testid }) => (
  <div data-testid={testid} className="rounded-2xl px-4 py-3 text-sm whitespace-pre-line max-w-md" style={{ background: "#E3F1EC", color: "#063B30", borderBottomLeftRadius: 4 }}>{text}</div>
);

const TemplateField = ({ id, label, value, onChange, cfg, hint }) => {
  const insert = (ph) => onChange(`${value}{${ph}}`);
  return (
    <div className="space-y-2">
      <label className={LABEL} style={{ color: "var(--sj-text-2)" }}>{label} <span style={{ color: "var(--sj-text-3)" }}>({700 - value.length} left)</span></label>
      <textarea data-testid={`${id}-input`} rows={5} maxLength={700} value={value} onChange={(e) => onChange(e.target.value)} className={`${INPUT} resize-y`} />
      <div className="flex flex-wrap gap-1.5 items-center">
        <span className="text-[11px]" style={{ color: "var(--sj-text-3)" }}>Insert:</span>
        {(cfg.placeholders || []).map((ph) => <button type="button" key={ph} data-testid={`${id}-ph-${ph}`} onClick={() => insert(ph)} className="text-[11px] px-2 py-0.5 rounded-full border hover:bg-[#F2F8F5]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-primary)" }}>{`{${ph}}`}</button>)}
      </div>
      {hint && <p className="text-[11px]" style={{ color: "var(--sj-text-3)" }}>{hint}</p>}
      <div className="text-[11px] font-semibold uppercase tracking-wider pt-1" style={{ color: "var(--sj-text-3)" }}>Preview</div>
      <Preview text={render(value, cfg)} testid={`${id}-preview`} />
    </div>
  );
};

export const FirstTimerMessagesSettings = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  useEffect(() => { api.get("/first-timer-messages").then(({ data }) => setCfg(data)).catch(() => {}); }, []);
  if (!cfg) return null;
  const set = (k) => (v) => setCfg({ ...cfg, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      const { service_times, address, welcome_template, nudge_days, nudge_template, visitor_thanks_days, visitor_thanks_template } = cfg;
      const { data } = await api.put("/first-timer-messages", { service_times, address, welcome_template, nudge_days: Number(nudge_days), nudge_template, visitor_thanks_days: Number(visitor_thanks_days), visitor_thanks_template });
      setCfg({ ...cfg, ...data }); toast.success("First-timer messages saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };
  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/first-timer-messages/run-nudges");
      const v = data.visitor_thanks || {};
      toast.success(`${data.skipped ? `Nudges not sent — ${data.skipped}` : `${data.nudged} nudge text${data.nudged === 1 ? "" : "s"} sent`} · ${v.skipped ? `Visitor thank-yous not sent — ${v.skipped}` : `${v.thanked || 0} visitor thank-you${v.thanked === 1 ? "" : "s"} sent`}`);
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setRunning(false);
  };

  return (
    <div className={`${CARD} p-6 space-y-5`} data-testid="first-timer-messages-card">
      <div>
        <h2 className="font-head text-xl font-semibold flex items-center gap-2" style={{ color: "var(--sj-text)" }}><Sparkles className="h-5 w-5" style={{ color: "var(--sj-primary)" }} /> First-timer messages</h2>
        <p className="text-sm mt-1" style={{ color: "var(--sj-text-2)" }}>The welcome text sent right after a visitor connects, the friendly second-visit nudge, and the thank-you for "Just visiting" sign-ups. Turn either on or off on the <Link to="/alerts" className="font-semibold hover:underline" style={{ color: "var(--sj-primary)" }}>Alerts</Link> page.</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div><label className={LABEL} style={{ color: "var(--sj-text-2)" }}>Service times</label><input data-testid="ft-service-times-input" maxLength={160} value={cfg.service_times} onChange={(e) => set("service_times")(e.target.value)} className={`${INPUT} mt-1`} /></div>
        <div><label className={LABEL} style={{ color: "var(--sj-text-2)" }}>Church address</label><input data-testid="ft-address-input" maxLength={160} value={cfg.address} onChange={(e) => set("address")(e.target.value)} className={`${INPUT} mt-1`} /></div>
      </div>
      <TemplateField id="ft-welcome" label="Welcome text" value={cfg.welcome_template} onChange={set("welcome_template")} cfg={cfg} />
      <div className="border-t pt-5 space-y-4" style={{ borderColor: "var(--sj-border)" }}>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className={LABEL} style={{ color: "var(--sj-text-2)" }}>Send the second-visit nudge</label>
            <select data-testid="ft-nudge-days" value={cfg.nudge_days} onChange={(e) => set("nudge_days")(e.target.value)} className={`${INPUT} mt-1 w-auto`}>
              {[3, 5, 7, 10, 14, 21].map((d) => <option key={d} value={d}>{`${d} days after their visit`}</option>)}
            </select>
          </div>
          <p className="text-xs pb-3" style={{ color: "var(--sj-text-3)" }}>Only to first-timers pastoral care hasn't reached yet (no "Spoke / reached", "Met in person" or reply logged). Sent once, checked every day at 10am.</p>
        </div>
        <TemplateField id="ft-nudge" label="Second-visit nudge text" value={cfg.nudge_template} onChange={set("nudge_template")} cfg={cfg} />
      </div>
      <div className="border-t pt-5 space-y-4" style={{ borderColor: "var(--sj-border)" }} data-testid="ft-visitor-thanks-section">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className={LABEL} style={{ color: "var(--sj-text-2)" }}>Send the visitor thank-you</label>
            <select data-testid="ft-visitor-thanks-days" value={cfg.visitor_thanks_days} onChange={(e) => set("visitor_thanks_days")(e.target.value)} className={`${INPUT} mt-1 w-auto`}>
              {[1, 2, 3, 4, 5, 7, 10, 14].map((d) => <option key={d} value={d}>{`${d} day${d > 1 ? "s" : ""} after they sign up`}</option>)}
            </select>
          </div>
          <p className="text-xs pb-3" style={{ color: "var(--sj-text-3)" }}>To people who sign up as "Just visiting" and are still in Visitor Care. Sent once, checked every day at 10am.</p>
        </div>
        <TemplateField id="ft-visitor-thanks" label="Visitor thank-you text" value={cfg.visitor_thanks_template} onChange={set("visitor_thanks_template")} cfg={cfg} />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button data-testid="ft-save-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-white font-semibold text-sm disabled:opacity-60" style={{ background: "var(--sj-primary)" }}><Save className="h-4 w-4" /> {saving ? "Saving…" : "Save first-timer messages"}</button>
        <button data-testid="ft-run-nudges-btn" onClick={runNow} disabled={running} className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold border disabled:opacity-60" style={{ borderColor: "var(--sj-border)", color: "var(--sj-primary)" }}><Send className="h-4 w-4" /> {running ? "Checking…" : "Send due texts now"}</button>
        {cfg.updated_by && <span className="text-xs" style={{ color: "var(--sj-text-3)" }}>Last updated by {cfg.updated_by}{cfg.updated_at ? ` on ${new Date(cfg.updated_at).toLocaleDateString("en-GB")}` : ""}</span>}
      </div>
    </div>
  );
};
